import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LegalSidePanel from '@/components/LegalSidePanel';

const SectionHeading = ({ number, title }) => (
  <h2 className="text-lg font-bold mb-3 mt-8 first:mt-0" style={{ color: '#131B2E' }}>
    {number}. {title}
  </h2>
);

const SubHeading = ({ children }) => (
  <p className="text-sm font-semibold mb-2 mt-4" style={{ color: '#131B2E' }}>
    {children}
  </p>
);

const Paragraph = ({ children }) => (
  <p className="text-sm leading-relaxed mb-3" style={{ color: '#475569' }}>
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 mb-3 pl-4">
    {items.map((item, i) => (
      <li key={i} className="text-sm leading-relaxed flex items-start gap-2" style={{ color: '#475569' }}>
        <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: '#0A3D91' }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function PrivacyPolicyPage({ onOpenContactModal }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="privacy-policy-page" style={{ backgroundColor: '#FFFFFF' }}>
      <Helmet>
        <title>Privacy Policy | KritvaSoft</title>
        <meta name="description" content="KritvaSoft Privacy Policy — how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://www.kritvasoft.com/privacy-policy" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium mb-6 transition-colors"
            style={{ color: '#475569' }}
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Left Column — Legal Content */}
            <div className="lg:col-span-8">

          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-1"
            style={{ color: '#131B2E' }}
            data-testid="privacy-headline"
          >
            Privacy Policy
          </h1>
          <p className="text-sm mb-3" style={{ color: '#475569' }}>
            Effective Date: March 2026
          </p>
          <div className="w-12 h-0.5 mb-6" style={{ backgroundColor: '#0A3D91' }} />

          <Paragraph>
            KritvaSoft (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) respects your privacy and is committed to protecting the information you share with us. This Privacy Policy explains how we collect, use, store, and protect your data when you interact with our website and services.
          </Paragraph>

          <div className="space-y-0" data-testid="privacy-content">
            {/* Section 1 */}
            <SectionHeading number={1} title="Information We Collect" />
            <SubHeading>a. Information You Provide</SubHeading>
            <Paragraph>When you contact us, submit a form, or book a consultation, we may collect:</Paragraph>
            <BulletList items={[
              'Name',
              'Email address',
              'Phone number',
              'Company name',
              'Project details and requirements',
              'Budget and timeline information',
            ]} />

            <SubHeading>b. Automatically Collected Information</SubHeading>
            <Paragraph>When you visit our website, we may automatically collect:</Paragraph>
            <BulletList items={[
              'IP address',
              'Browser type and version',
              'Device information and operating system',
              'Pages visited and navigation paths',
              'Usage patterns and session duration',
            ]} />

            <SubHeading>c. Cookies and Tracking Technologies</SubHeading>
            <Paragraph>
              We use cookies and similar technologies to improve website performance, analyze traffic patterns, and enhance user experience. You can manage cookie preferences through your browser settings at any time.
            </Paragraph>

            {/* Section 2 */}
            <SectionHeading number={2} title="How We Use Your Information" />
            <Paragraph>We use the data we collect to:</Paragraph>
            <BulletList items={[
              'Respond to inquiries and support requests',
              'Provide, deliver, and improve our services',
              'Schedule and manage consultations',
              'Improve website performance and user experience',
              'Analyze usage behavior and optimize content',
              'Send relevant communications about our services',
            ]} />
            <Paragraph>
              We do not sell, rent, or trade your personal data to third parties under any circumstances.
            </Paragraph>

            {/* Section 3 */}
            <SectionHeading number={3} title="Third-Party Services" />
            <Paragraph>We may use trusted third-party tools and services, including:</Paragraph>
            <BulletList items={[
              'Analytics platforms (e.g., Google Analytics)',
              'Cloud hosting and infrastructure providers',
              'Scheduling tools (e.g., Calendly)',
              'Email delivery and CRM systems',
            ]} />
            <Paragraph>
              These services may process data according to their own privacy policies. We only share the minimum data necessary for them to function.
            </Paragraph>

            {/* Section 4 */}
            <SectionHeading number={4} title="Data Retention" />
            <Paragraph>
              We retain personal data only as long as necessary for legitimate business, legal, and operational purposes. When data is no longer needed, it is securely deleted or anonymized.
            </Paragraph>

            {/* Section 5 */}
            <SectionHeading number={5} title="Data Security" />
            <Paragraph>
              We implement appropriate technical and organizational security measures to protect your data, including encryption in transit and at rest, role-based access controls, and regular security audits. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.
            </Paragraph>

            {/* Section 6 */}
            <SectionHeading number={6} title="Your Rights" />
            <Paragraph>Depending on your jurisdiction, you may have the right to:</Paragraph>
            <BulletList items={[
              'Access the personal data we hold about you',
              'Request correction of inaccurate data',
              'Request deletion of your data',
              'Opt out of marketing communications',
              'Withdraw consent where applicable',
            ]} />
            <Paragraph>
              To exercise any of these rights, please contact us using the details provided below.
            </Paragraph>

            {/* Section 7 */}
            <SectionHeading number={7} title="International Data Handling" />
            <Paragraph>
              Your data may be processed and stored in jurisdictions outside your country of residence, depending on the location of our service providers. We take appropriate steps to ensure your data receives an adequate level of protection wherever it is processed.
            </Paragraph>

            {/* Section 8 */}
            <SectionHeading number={8} title="External Links" />
            <Paragraph>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those external sites. We encourage you to review their policies independently.
            </Paragraph>

            {/* Section 9 */}
            <SectionHeading number={9} title="Updates to This Policy" />
            <Paragraph>
              We may update this Privacy Policy periodically to reflect changes in our practices, legal requirements, or services. Updates will be posted on this page with a revised effective date.
            </Paragraph>

            {/* Section 10 */}
            <SectionHeading number={10} title="Contact" />
            <Paragraph>
              If you have questions or concerns about this Privacy Policy, please contact us:
            </Paragraph>
            <div
              className="mt-3 p-4 rounded-lg border"
              style={{ borderColor: '#E2E8F0', backgroundColor: 'rgba(10, 61, 145, 0.02)' }}
            >
              <p className="text-sm font-bold mb-1" style={{ color: '#131B2E' }}>KritvaSoft</p>
              <p className="text-sm" style={{ color: '#475569' }}>
                Email:{' '}
                <a href="mailto:privacy@kritvasoft.com" className="font-medium" style={{ color: '#0A3D91' }}>
                  privacy@kritvasoft.com
                </a>
              </p>
              <p className="text-sm" style={{ color: '#475569' }}>
                Website:{' '}
                <a href="https://kritvasoft.com" className="font-medium" style={{ color: '#0A3D91' }}>
                  kritvasoft.com
                </a>
              </p>
            </div>
          </div>
            </div>

            {/* Right Column — Sticky Panel */}
            <div className="lg:col-span-4">
              <LegalSidePanel onOpenContactModal={onOpenContactModal} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
